import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, useSpring } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, Clock, BellRing } from "lucide-react";
import { ProductIntroProps } from "./product-intro/types";
import fa from "./locales/ProductIntro-fa.json";
import en from "./locales/ProductIntro-en.json";
import ar from "./locales/ProductIntro-ar.json";
import tr from "./locales/ProductIntro-tr.json";

// ─── Floating Particle ──────────────────────────────────────────────────────
const FloatingParticle: React.FC<{ x: number; delay: number; color: string }> = ({ x, delay, color }) => (
    <motion.div
        className="absolute w-1 h-1 rounded-full pointer-events-none"
        style={{ left: `${x}%`, bottom: "10%", backgroundColor: color }}
        animate={{ y: [-0, -35, -0], opacity: [0, 0.7, 0], scale: [0, 1.2, 0] }}
        transition={{ duration: 1.8, delay, repeat: Infinity, ease: "easeInOut" }}
    />
);

// ─── Pulse Ring ──────────────────────────────────────────────────────────────
const PulseRing: React.FC<{ color: string }> = ({ color }) => (
    <>
        {[0, 0.4, 0.8].map((d) => (
            <motion.div
                key={d}
                className="absolute inset-0 rounded-[10px]"
                style={{ border: `1.5px solid ${color}` }}
                initial={{ opacity: 0.5, scale: 1 }}
                animate={{ opacity: 0, scale: 1.6 }}
                transition={{ duration: 1.6, delay: d, repeat: Infinity, ease: "easeOut" }}
            />
        ))}
    </>
);

// ─── Progress Dot ────────────────────────────────────────────────────────────
const ProgressDot: React.FC<{ active: boolean; color: string; onClick: () => void }> = ({ active, color, onClick }) => (
    <motion.button
        className="rounded-full cursor-pointer"
        style={{ backgroundColor: active ? color : "#e5e7eb" }}
        animate={{ width: active ? 20 : 8, height: 6 }}
        transition={{ duration: 0.3 }}
        onClick={onClick}
        whileHover={{ scale: 1.2 }}
        whileTap={{ scale: 0.9 }}
    />
);

// ─── Tilt Image Container ───────────────────────────────────────────────────
const TiltImage: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => {
    const ref = useRef<HTMLDivElement>(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotateX = useTransform(y, [-0.5, 0.5], [5, -5]);
    const rotateY = useTransform(x, [-0.5, 0.5], [-5, 5]);
    const springConfig = { stiffness: 150, damping: 20 };
    const springRotateX = useSpring(rotateX, springConfig);
    const springRotateY = useSpring(rotateY, springConfig);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        x.set((e.clientX - rect.left) / rect.width - 0.5);
        y.set((e.clientY - rect.top) / rect.height - 0.5);
    };
    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            ref={ref}
            className={className}
            style={{ rotateX: springRotateX, rotateY: springRotateY, transformStyle: "preserve-3d" }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            {children}
        </motion.div>
    );
};

// ─── Feature Card ────────────────────────────────────────────────────────────
interface FeatureCardInternalProps {
    feature: { icon: React.ReactNode; title: string; description: string };
    index: number;
    activeTab: string;
    themeColors: ReturnType<typeof getThemeColorsHelper>;
    t: any;
}

function getThemeColorsHelper(theme?: string) {
    if (theme === "orange") {
        return {
            primary: "#FF6700",
            secondary: "#F5A269",
            gradient: "linear-gradient(180deg, #FF6700 0%, #E65C00 100%)",
            textGradient: "linear-gradient(281deg, #FF6700 0%, #993E00 100%)",
            BgIcon: "linear-gradient(180deg, #FFECDF 0%, rgba(255, 236, 223, 0.50) 100%)",
            primaryAlpha: "rgba(255,103,0,0.12)",
            primaryAlpha2: "rgba(255,103,0,0.06)",
            primaryRing: "rgba(255,103,0,0.3)",
            glow: "rgba(255,103,0,0.18)",
        };
    }
    if (theme === "pink") {
        return {
            primary: "#FF8EA6",
            secondary: "#FFA6B9",
            gradient: "linear-gradient(180deg, #FF8EA6 0%, #E85678 100%)",
            textGradient: "linear-gradient(281deg, #FF6F8F 0%, #C92A54 100%)",
            BgIcon: "linear-gradient(180deg, #FFF0F3 0%, rgba(255, 240, 243, 0.50) 100%)",
            primaryAlpha: "rgba(255,142,166,0.18)",
            primaryAlpha2: "rgba(255,142,166,0.08)",
            primaryRing: "rgba(255,142,166,0.4)",
            glow: "rgba(255,142,166,0.25)",
        };
    }
    if (theme === "green") {
        return {
            primary: "#4DCE7C",
            secondary: "#6EE7B7",
            gradient: "linear-gradient(180deg, #4DCE7C 0%, #10B981 100%)",
            textGradient: "linear-gradient(281deg, #4DCE7C 0%, #059669 100%)",
            BgIcon: "linear-gradient(180deg, #E6FDF0 0%, rgba(230, 253, 240, 0.50) 100%)",
            primaryAlpha: "rgba(77,206,124,0.18)",
            primaryAlpha2: "rgba(77,206,124,0.08)",
            primaryRing: "rgba(77,206,124,0.4)",
            glow: "rgba(77,206,124,0.25)",
        };
    }
    return {
        primary: "#155DFC",
        secondary: "#3B82F6",
        gradient: "linear-gradient(180deg, #155DFC 0%, #0038A8 100%)",
        textGradient: "linear-gradient(281deg, #155DFC 0%, #0038A8 100%)",
        BgIcon: "linear-gradient(180deg, #E0EDFF 0%, rgba(224, 237, 255, 0.50) 100%)",
        primaryAlpha: "rgba(21,93,252,0.12)",
        primaryAlpha2: "rgba(21,93,252,0.06)",
        primaryRing: "rgba(21,93,252,0.3)",
        glow: "rgba(21,93,252,0.18)",
    };
}

const FeatureCard: React.FC<FeatureCardInternalProps> = ({ feature, index, activeTab, themeColors, t }) => {
    const [hovered, setHovered] = useState(false);
    const [particles, setParticles] = useState<{ id: number; x: number }[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const spawnParticles = () => {
        const ps = Array.from({ length: 6 }, (_, i) => ({ id: Date.now() + i, x: 10 + Math.random() * 80 }));
        setParticles(ps);
        timerRef.current = setTimeout(() => setParticles([]), 2000);
    };
    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        []
    );

    return (
        <motion.div
            key={`${activeTab}-${index}`}
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.08, type: "spring", stiffness: 200 }}
            className="relative flex items-center gap-3 sm:gap-4 p-3 sm:p-3.5 md:p-4 rounded-2xl sm:rounded-[22px] cursor-pointer overflow-hidden group w-full min-w-0"
            style={{
                background: hovered
                    ? `linear-gradient(135deg, rgba(255,255,255,0.98) 0%, ${themeColors.primaryAlpha2} 100%)`
                    : "rgba(255,255,255,0.88)",
                border: `1px solid ${hovered ? themeColors.primaryAlpha : "rgba(255,255,255,0.9)"}`,
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                boxShadow: hovered
                    ? `0 16px 36px -6px ${themeColors.glow}, 0 2px 10px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.9)`
                    : "0 2px 6px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.8)",
                transition: "all 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
            onMouseEnter={() => {
                setHovered(true);
                spawnParticles();
            }}
            onMouseLeave={() => setHovered(false)}
            whileHover={{ scale: 1.02, y: -3 }}
            whileTap={{ scale: 0.98 }}
        >
            {/* Top shimmer line */}
            <motion.div
                className="absolute top-0 left-3 right-3 h-[1.5px] rounded-full pointer-events-none"
                style={{ background: `linear-gradient(90deg, transparent, ${themeColors.primary}, transparent)` }}
                animate={{ opacity: hovered ? 0.8 : 0, scaleX: hovered ? 1 : 0 }}
                transition={{ duration: 0.35 }}
            />

            {/* Light glare sweep effect on hover */}
            <motion.div
                className="absolute inset-0 pointer-events-none rounded-2xl sm:rounded-[22px]"
                style={{
                    background: "linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.4) 50%, transparent 80%)",
                    backgroundSize: "200% 100%",
                }}
                animate={hovered ? { backgroundPosition: ["100% 0", "-100% 0"] } : { backgroundPosition: "100% 0" }}
                transition={{ duration: 0.75, ease: "easeInOut" }}
            />

            {/* Floating Particles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {particles.map((p) => (
                    <FloatingParticle key={p.id} x={p.x} delay={0} color={themeColors.primary} />
                ))}
            </div>

            {/* Icon */}
            <motion.div
                className="relative w-11 h-11 sm:w-12 sm:h-12 md:w-[50px] md:h-[50px] rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-300"
                style={{
                    background: hovered ? themeColors.BgIcon : "linear-gradient(180deg, rgba(23,31,53,0.08) 0%, rgba(23,31,53,0.02) 100%)",
                    boxShadow: hovered ? `0 8px 20px -4px ${themeColors.glow}` : "none",
                }}
                animate={{ rotate: hovered ? [0, -10, 10, -5, 0] : 0, scale: hovered ? 1.12 : 1 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
            >
                {hovered && <PulseRing color={themeColors.primaryRing} />}
                <motion.div
                    className="transition-all duration-300 flex items-center justify-center"
                    style={{
                        color: hovered ? themeColors.primary : "#222738",
                    }}
                >
                    {feature.icon}
                </motion.div>
            </motion.div>

            {/* Text */}
            <div className="flex-1 min-w-0 flex flex-col relative z-10 text-right">
                <motion.h3
                    className="text-[13px] sm:text-sm md:text-base font-bold leading-5 sm:leading-6 tracking-tight transition-all duration-300 break-words"
                    style={{
                        ...(hovered
                            ? {
                                  background: themeColors.textGradient,
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  backgroundClip: "text",
                              }
                            : { color: "#222738" }),
                    }}
                    animate={{ x: hovered ? -2 : 0 }}
                    transition={{ duration: 0.2 }}
                >
                    {t(feature.title, { ns: "productIntro" })}
                </motion.h3>
                <motion.p
                    className="text-[11px] sm:text-xs md:text-sm font-normal text-right leading-4 sm:leading-5 tracking-tight transition-colors duration-300 break-words mt-0.5 line-clamp-2 sm:line-clamp-none"
                    animate={{ color: hovered ? "#374151" : "#5D697F" }}
                >
                    {t(feature.description, { ns: "productIntro" })}
                </motion.p>
            </div>

            {/* Bottom glow */}
            <motion.div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[20px] sm:h-[24px] w-[60%] rounded-full blur-xl pointer-events-none"
                style={{ background: `radial-gradient(ellipse, ${themeColors.glow} 0%, transparent 70%)` }}
                animate={{ opacity: hovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
            />
        </motion.div>
    );
};

// ─── Tab Button ──────────────────────────────────────────────────────────────
interface TabButtonProps {
    tab: { key: string; label: string; icon: React.ReactNode };
    isActive: boolean;
    themeColors: ReturnType<typeof getThemeColorsHelper>;
    onClick: () => void;
    t: any;
}

const TabButton: React.FC<TabButtonProps> = ({ tab, isActive, themeColors, onClick, t }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <motion.button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className="relative flex-1 min-w-[95px] sm:min-w-[120px] h-10 sm:h-12 md:h-[50px] rounded-xl flex items-center justify-center gap-1.5 sm:gap-2.5 px-2 sm:px-4 md:px-6 cursor-pointer overflow-hidden group shrink-0"
            style={{
                background: isActive
                    ? "rgba(255,255,255,0.95)"
                    : hovered
                    ? "rgba(255,255,255,0.6)"
                    : "transparent",
                border: `1px solid ${isActive ? "rgba(255,255,255,0.95)" : "transparent"}`,
                backdropFilter: isActive ? "blur(12px)" : "none",
                WebkitBackdropFilter: isActive ? "blur(12px)" : "none",
                boxShadow: isActive
                    ? `0 6px 20px rgba(217,217,217,0.6), 0 1px 0 rgba(255,255,255,0.9) inset`
                    : "none",
            }}
            whileHover={{ scale: isActive ? 1.01 : 1.03 }}
            whileTap={{ scale: 0.96 }}
        >
          

            {/* Icon */}
            <div className="relative w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 shrink-0 flex items-center justify-center">
                {isActive && (
                    <motion.div
                        className="absolute inset-0 w-full h-full blur-[8px]"
                        style={{ color: themeColors.primary, opacity: 0.6 }}
                        animate={{ scale: [1, 1.25, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        {tab.icon}
                    </motion.div>
                )}
                <motion.div
                    className="absolute inset-0 w-full h-full flex items-center justify-center"
                    style={{ color: isActive ? themeColors.primary : hovered ? themeColors.secondary : "#B1B5C2" }}
                    animate={{ rotate: isActive ? [0, 8, -8, 0] : hovered ? [0, -10, 10, 0] : 0, scale: hovered ? 1.15 : 1 }}
                    transition={{ duration: 0.4 }}
                >
                    {tab.icon}
                </motion.div>
            </div>

            {/* Label */}
            <motion.span
                className="text-xs sm:text-sm md:text-base font-semibold sm:font-medium transition-colors duration-200 truncate whitespace-nowrap"
                animate={{ color: isActive ? "#111827" : hovered ? "#374151" : "#6b7280" }}
            >
                {t(tab.label, { ns: "productIntro" })}
            </motion.span>
        </motion.button>
    );
};

// ─── Product Image Card Component with Load Animation & Micro-Action Hover ─────
interface ProductImageCardProps {
    src: string;
    themeColors: ReturnType<typeof getThemeColorsHelper>;
    data: any;
    t: any;
    ctaRef: React.RefObject<HTMLButtonElement | null>;
    springCtaX: any;
    springCtaY: any;
    handleCtaMove: (e: React.MouseEvent<HTMLButtonElement>) => void;
    handleCtaLeave: () => void;
    ctaHovered: boolean;
    setCtaHovered: (v: boolean) => void;
    handleCtaClick: (e: React.MouseEvent) => void;
}

const ProductImageCard: React.FC<ProductImageCardProps> = ({
    src,
    themeColors,
    data,
    t,
    ctaRef,
    springCtaX,
    springCtaY,
    handleCtaMove,
    handleCtaLeave,
    ctaHovered,
    setCtaHovered,
    handleCtaClick,
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [particles, setParticles] = useState<{ id: number; x: number; y: number }[]>([]);
    const particleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setIsLoaded(false);
        const img = new Image();
        img.src = src;
        if (img.complete) {
            setIsLoaded(true);
        } else {
            img.onload = () => setIsLoaded(true);
        }
    }, [src]);

    const spawnPhotoParticles = () => {
        const ps = Array.from({ length: 5 }, (_, i) => ({
            id: Date.now() + i,
            x: 20 + Math.random() * 60,
            y: 20 + Math.random() * 60,
        }));
        setParticles(ps);
        if (particleTimerRef.current) clearTimeout(particleTimerRef.current);
        particleTimerRef.current = setTimeout(() => setParticles([]), 1800);
    };

    return (
        <div
            className="w-full h-[320px] sm:h-[420px] md:h-[500px] lg:h-[595px] rounded-[28px] sm:rounded-[36px] lg:rounded-[48px] relative overflow-hidden group cursor-pointer"
            style={{
                boxShadow: isHovered
                    ? `0 36px 90px -15px ${themeColors.glow}, 0 0 30px ${themeColors.primaryAlpha}`
                    : `0 30px 80px -20px ${themeColors.glow}`,
                transition: "box-shadow 0.5s ease",
            }}
            onMouseEnter={() => {
                setIsHovered(true);
                spawnPhotoParticles();
            }}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* ── 1. Load Animation / Skeleton Shimmer ── */}
            <AnimatePresence>
                {!isLoaded && (
                    <motion.div
                        className="absolute inset-0 z-30 flex flex-col items-center justify-center rounded-[28px] sm:rounded-[36px] lg:rounded-[48px] overflow-hidden"
                        style={{
                            background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #f8fafc 100%)",
                        }}
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                        {/* Shimmering sweep wave */}
                        <motion.div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.7) 50%, transparent 100%)",
                                backgroundSize: "200% 100%",
                            }}
                            animate={{ backgroundPosition: ["100% 0", "-100% 0"] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                        />

                        {/* Center pulsating loader spinner & icon */}
                        <div className="relative z-10 flex flex-col items-center gap-3">
                            <motion.div
                                className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                                style={{ background: themeColors.gradient }}
                                animate={{ scale: [0.95, 1.08, 0.95] }}
                                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <motion.div
                                    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
                                />
                            </motion.div>
                            <span
                                className="text-xs font-semibold tracking-wide"
                                style={{ color: themeColors.primary }}
                            >
                                در حال بارگذاری تصویر محصول...
                            </span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── 2. Product Image with Load Fade-In + Hover Micro-Zoom ── */}
            <motion.div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out"
                style={{
                    backgroundImage: `url("${src}")`,
                }}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{
                    opacity: isLoaded ? 1 : 0,
                    scale: isLoaded ? (isHovered ? 1.07 : 1) : 0.96,
                }}
                transition={{
                    opacity: { duration: 0.7, ease: "easeOut" },
                    scale: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
                }}
            />

            {/* ── 3. Micro-Action: Glass Shine Overlay & Sweep Beam on Hover ── */}
            <motion.div
                className="absolute inset-0 pointer-events-none rounded-[28px] sm:rounded-[36px] lg:rounded-[48px]"
                style={{
                    background:
                        "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 40%, transparent 60%, rgba(255,255,255,0.08) 100%)",
                }}
                animate={{ opacity: isHovered ? 1 : 0.6 }}
                transition={{ duration: 0.4 }}
            />

            {/* Interactive Gloss Sweep Beam */}
            <motion.div
                className="absolute top-0 bottom-0 w-1/2 pointer-events-none skew-x-12 z-20"
                style={{
                    background:
                        "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)",
                }}
                animate={isHovered ? { left: ["-60%", "140%"] } : { left: "-60%" }}
                transition={{ duration: 0.9, ease: "easeInOut" }}
            />

            {/* ── 4. Micro-Action: Floating Sparkle Particles on Hover ── */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
                {particles.map((p) => (
                    <motion.div
                        key={p.id}
                        className="absolute w-1.5 h-1.5 rounded-full pointer-events-none"
                        style={{
                            left: `${p.x}%`,
                            top: `${p.y}%`,
                            backgroundColor: themeColors.primary,
                            boxShadow: `0 0 8px ${themeColors.primary}`,
                        }}
                        initial={{ opacity: 0, scale: 0, y: 0 }}
                        animate={{ opacity: [0, 0.9, 0], scale: [0, 1.4, 0], y: -30 }}
                        transition={{ duration: 1.4, ease: "easeOut" }}
                    />
                ))}
            </div>

           

            {/* ── 6. Badge ── */}
            <motion.div
                className="hidden sm:flex absolute top-3 sm:top-[18px] ms-3 sm:ms-4 items-center gap-2 rounded-full px-2.5 sm:px-3 py-1.5 sm:py-2 z-20"
                style={{
                    background: "rgba(255,255,255,0.25)",
                    backdropFilter: "blur(16px)",
                    WebkitBackdropFilter: "blur(16px)",
                    border: "1px solid rgba(255,255,255,0.35)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.5)",
                }}
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                viewport={{ once: true }}
                animate={{ y: isHovered ? -3 : 0, scale: isHovered ? 1.04 : 1 }}
                whileHover={{ scale: 1.06, boxShadow: `0 8px 24px ${themeColors.glow}` }}
            >
                <div className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-white/40 shadow-md shrink-0">
                    <div className="w-7 h-7 sm:w-9 sm:h-9 bg-white relative rounded-full flex items-center justify-center overflow-hidden">
                        {data.badgeIcon ? (
                            <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                                {data.badgeIcon}
                            </div>
                        ) : (
                            <svg viewBox="0 0 44 43" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                                <rect x="4" y="2.142" width="36" height="36" rx="18" fill="#fff" />
                                <g opacity=".4" filter="url(#a)">
                                    <path
                                        d="M19.499 16.723a5.36 5.36 0 0 1 5.377 0l5.83 3.381a5.36 5.36 0 0 1 2.669 4.635v6.805a5.36 5.36 0 0 1-2.67 4.635l-5.829 3.381a5.36 5.36 0 0 1-5.377 0l-5.83-3.38A5.36 5.36 0 0 1 11 31.543V24.74a5.36 5.36 0 0 1 2.67-4.635zm7.075 15.06a.64.64 0 0 0-.86-.069c-1.096.85-2.065 1.537-3.557 1.546-1.49-.009-2.46-.696-3.556-1.546a.64.64 0 0 0-.86.07.63.63 0 0 0 .032.898c.997.895 2.29 1.81 4.355 1.827v.002h.06v-.002c2.065-.017 3.357-.932 4.354-1.827a.63.63 0 0 0 .032-.898m-8.137-9c-.591 0-1.072.48-1.072 1.072v4.048a1.073 1.073 0 0 0 2.144 0v-4.048c0-.592-.48-1.071-1.072-1.071m7.382 0c-.591 0-1.07.48-1.071 1.072v4.049a1.071 1.071 0 0 0 2.143 0v-4.049c0-.592-.48-1.071-1.072-1.071"
                                        fill={`url(#b-${data.id})`}
                                    />
                                </g>
                                <path
                                    d="M19.312 8.723a5.36 5.36 0 0 1 5.377 0l5.829 3.381a5.36 5.36 0 0 1 2.67 4.635v6.805a5.36 5.36 0 0 1-2.67 4.635l-5.83 3.38a5.36 5.36 0 0 1-5.376 0l-5.83-3.38a5.36 5.36 0 0 1-2.67-4.635v-6.805a5.36 5.36 0 0 1 2.67-4.635zm7.075 15.06a.64.64 0 0 0-.86-.07c-1.097.85-2.065 1.538-3.557 1.547-1.491-.01-2.46-.697-3.557-1.546a.64.64 0 0 0-.86.07.63.63 0 0 0 .033.898c.997.894 2.289 1.81 4.354 1.827v.002l.03-.001H22v-.001c2.066-.018 3.358-.933 4.355-1.827a.63.63 0 0 0 .032-.899m-8.137-9c-.592 0-1.072.48-1.072 1.072v4.048a1.072 1.072 0 0 0 2.143 0v-4.047c0-.592-.48-1.073-1.071-1.073m7.382.001c-.592 0-1.072.48-1.072 1.071v4.048a1.071 1.071 0 1 0 2.143 0v-4.047c0-.592-.48-1.072-1.071-1.072"
                                    fill={`url(#c-${data.id})`}
                                />
                                <defs>
                                    <linearGradient id={`b-${data.id}`} x1="22.188" y1="15.999" x2="22.188" y2="40.283" gradientUnits="userSpaceOnUse">
                                        <stop stopColor={themeColors.primary} />
                                        <stop offset="1" stopColor={themeColors.secondary} />
                                    </linearGradient>
                                    <linearGradient id={`c-${data.id}`} x1="22" y1="8" x2="22" y2="32.283" gradientUnits="userSpaceOnUse">
                                        <stop stopColor={themeColors.primary} />
                                        <stop offset="1" stopColor={themeColors.secondary} />
                                    </linearGradient>
                                    <filter id="a" x="-5" y="0" width="54.375" height="56.283" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                                        <feGaussianBlur stdDeviation="8" result="effect1_foregroundBlur" />
                                    </filter>
                                </defs>
                            </svg>
                        )}
                    </div>
                </div>
                <div className="min-w-0">
                    <div className="text-[11px] sm:text-xs text-neutral-700 font-normal truncate">{t(data.supportText, { ns: "productIntro" })}</div>
                    <div className="text-xs sm:text-sm text-neutral-900 font-bold truncate">{t(data.smartAssistantText, { ns: "productIntro" })}</div>
                </div>
            </motion.div>

            {/* ── 7. CTA — Magnetic Button ── */}
            <motion.button
                ref={ctaRef}
                className="absolute bottom-4 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8 h-12 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center gap-2 sm:gap-3 text-white font-bold text-sm sm:text-base md:text-lg cursor-pointer overflow-hidden z-20"
                style={{ x: springCtaX, y: springCtaY }}
                onMouseMove={handleCtaMove}
                onMouseLeave={handleCtaLeave}
                onMouseEnter={() => setCtaHovered(true)}
                whileTap={{ scale: 0.97 }}
            >
                {/* Base gradient */}
                <div className="absolute inset-0 transition-opacity duration-300" style={{ background: themeColors.gradient, opacity: ctaHovered ? 0 : 1 }} />
                {/* Hover gradient */}
                <div
                    className="absolute inset-0 transition-opacity duration-300"
                    style={{
                        background: data.theme === "pink"
                            ? "linear-gradient(135deg, #FFA6B9, #FF8EA6, #E85678)"
                            : data.theme === "orange"
                            ? "linear-gradient(135deg, #FF8533, #FF6700, #E65C00)"
                            : data.theme === "green"
                            ? "linear-gradient(135deg, #6EE7B7, #4DCE7C, #10B981)"
                            : "linear-gradient(135deg, #3B82F6, #155DFC, #0038A8)",
                        opacity: ctaHovered ? 1 : 0,
                    }}
                />
                {/* Shimmer */}
                <motion.div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)",
                        backgroundSize: "250% 100%",
                    }}
                    animate={ctaHovered ? { backgroundPosition: ["200% 0", "-200% 0"] } : {}}
                    transition={{ duration: 0.7, ease: "easeInOut" }}
                />
                {/* Border glow */}
                <div
                    className="absolute inset-0 rounded-xl sm:rounded-2xl"
                    style={{
                        border: "1px solid rgba(255,255,255,0.25)",
                        boxShadow: ctaHovered ? `0 12px 30px -4px ${themeColors.glow}` : `0 6px 16px -4px ${themeColors.glow}`,
                    }}
                />

                <a
                    href={data.isExternal || data.ctaLink?.startsWith("http") ? data.ctaLink : (data.id === "elderlyWatch" ? "#" : data.ctaLink)}
                    target={data.isExternal || data.ctaLink?.startsWith("http") ? "_blank" : undefined}
                    rel={data.isExternal || data.ctaLink?.startsWith("http") ? "noopener noreferrer" : undefined}
                    onClick={handleCtaClick}
                    className="relative z-10 flex items-center gap-2"
                >
                    <span className="ml-1">{t(data.ctaText, { ns: "productIntro" })}</span>
                    <motion.svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        animate={{ x: ctaHovered ? [0, -5, 0] : [0, -3, 0] }}
                        transition={{ duration: ctaHovered ? 0.6 : 1.5, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <path d="M8.99996 16.9998L4 11.9997L9 6.99976" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4 12H20" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </motion.svg>
                </a>
            </motion.button>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const ProductIntro: React.FC<ProductIntroProps> = ({ data, className = "" }) => {
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState<string>(data.defaultTab && data.features[data.defaultTab] ? data.defaultTab : Object.keys(data.features)[0]);
    const tabKeys = Object.keys(data.features);
    const [ctaHovered, setCtaHovered] = useState(false);
    const [isRestockModalOpen, setIsRestockModalOpen] = useState(false);

    const handleCtaClick = (e: React.MouseEvent) => {
        if (data.isExternal || data.ctaLink?.startsWith("http")) {
            e.preventDefault();
            e.stopPropagation();
            window.open(data.ctaLink, "_blank", "noopener,noreferrer");
        } else if (data.id === "elderlyWatch") {
            e.preventDefault();
            e.stopPropagation();
            setIsRestockModalOpen(true);
        }
    };

    // Magnetic CTA
    const ctaRef = useRef<HTMLButtonElement>(null);
    const ctaX = useMotionValue(0);
    const ctaY = useMotionValue(0);
    const springCtaX = useSpring(ctaX, { stiffness: 180, damping: 16 });
    const springCtaY = useSpring(ctaY, { stiffness: 180, damping: 16 });

    const handleCtaMove = (e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = ctaRef.current?.getBoundingClientRect();
        if (!rect) return;
        ctaX.set((e.clientX - rect.left - rect.width / 2) * 0.2);
        ctaY.set((e.clientY - rect.top - rect.height / 2) * 0.2);
    };
    const handleCtaLeave = () => {
        ctaX.set(0);
        ctaY.set(0);
        setCtaHovered(false);
    };

    useEffect(() => {
        i18n.addResourceBundle("fa", "productIntro", fa, true, true);
        i18n.addResourceBundle("en", "productIntro", en, true, true);
        i18n.addResourceBundle("ar", "productIntro", ar, true, true);
        i18n.addResourceBundle("tr", "productIntro", tr, true, true);
    }, []);

    const themeColors = getThemeColorsHelper(data.theme);
    const isReversed = data.reverse !== undefined ? data.reverse : data.theme === "blue";
    const flexDirection = isReversed ? "md:flex-row-reverse" : "md:flex-row";

    return (
        <section className={`relative overflow-hidden ${className}`}>
            {/* Ambient background */}
            <motion.div
                className="absolute pointer-events-none"
                style={{
                    width: 500,
                    height: 500,
                    borderRadius: "50%",
                    background: `radial-gradient(ellipse, ${themeColors.primaryAlpha2} 0%, transparent 70%)`,
                    filter: "blur(50px)",
                    top: "10%",
                    left: isReversed ? "60%" : "10%",
                }}
                animate={{ scale: [1, 1.2, 1], x: [0, 20, 0] }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative z-10 w-full px-2.5 sm:px-6 lg:px-8 max-w-7xl mx-auto py-6 sm:py-10 lg:py-16">
                <div className={`flex flex-col-reverse ${flexDirection} gap-6 lg:gap-12 items-center w-full`}>
                    {/* ── Left: Product Image ── */}
                    <motion.div
                        className="relative order-2 lg:order-1 w-full lg:w-[496px] max-w-full"
                        initial={{ opacity: 0, x: isReversed ? 50 : -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <TiltImage className="w-full max-w-[496px] mx-auto">
                            <ProductImageCard
                                src={data.backgroundImage}
                                themeColors={themeColors}
                                data={data}
                                t={t}
                                ctaRef={ctaRef}
                                springCtaX={springCtaX}
                                springCtaY={springCtaY}
                                handleCtaMove={handleCtaMove}
                                handleCtaLeave={handleCtaLeave}
                                ctaHovered={ctaHovered}
                                setCtaHovered={setCtaHovered}
                                handleCtaClick={handleCtaClick}
                            />
                        </TiltImage>
                    </motion.div>

                    {/* ── Right: Content ── */}
                    <div className="w-full lg:w-[740px] max-w-full flex flex-col gap-4 sm:gap-6 lg:gap-8">
                        {/* Text */}
                        <div className="flex mb-1 sm:mb-4 flex-col gap-2 sm:gap-5 relative z-10">
                            {/* Tag */}
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                                className="hidden md:block text-base sm:text-lg font-medium"
                                style={{
                                    background: themeColors.textGradient,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                {t(data.tagText, { ns: "productIntro" })}
                            </motion.div>

                            {/* Heading */}
                            <motion.div
                                className="flex flex-col"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 0.3 }}
                                viewport={{ once: true }}
                            >
                                <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2 flex-wrap">
                                    <h2 className="text-xl sm:text-3xl md:text-[34px] font-bold text-neutral-900 leading-tight">{t(data.headingPart2, { ns: "productIntro" })}</h2>
                                    <motion.h2
                                        className="text-xl sm:text-3xl md:text-[34px] font-bold leading-tight px-1 py-0.5 inline-block"
                                        style={{
                                            background: themeColors.textGradient,
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                        whileHover={{ scale: 1.04 }}
                                        transition={{ type: "spring", stiffness: 400 }}
                                    >
                                        {t(data.headingPart1, { ns: "productIntro" })}
                                    </motion.h2>
                                </div>
                                <h3 className="text-lg sm:text-3xl md:text-[34px] font-normal text-neutral-900 leading-tight">{t(data.headingPart3, { ns: "productIntro" })}</h3>
                            </motion.div>

                            {/* Description */}
                            <motion.p
                                className="text-xs sm:text-sm md:text-base text-neutral-700 text-right leading-relaxed tracking-tight sm:tracking-[-0.32px]"
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                                viewport={{ once: true }}
                            >
                                {t(data.descriptionText, { ns: "productIntro" })}
                            </motion.p>
                        </div>

                        {/* Tabs + Features */}
                        <motion.div
                            className="flex flex-col gap-3 sm:gap-4 p-2.5 sm:p-3.5 md:p-4 rounded-2xl sm:rounded-3xl lg:rounded-[32px] relative overflow-hidden w-full max-w-full"
                            style={{
                                background: "rgba(255,255,255,0.4)",
                                border: "1.5px solid rgba(255,255,255,0.7)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                boxShadow: "0 4px 24px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.9)",
                            }}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.6 }}
                            viewport={{ once: true }}
                        >
                            {/* Inner glass border */}
                            <div
                                className="absolute inset-0 rounded-2xl sm:rounded-3xl lg:rounded-[32px] pointer-events-none"
                                style={{ border: "1px solid rgba(23,31,53,0.04)" }}
                            />

                            {/* Tabs */}
                            <div className="relative w-full">
                                <div
                                    className="flex items-center gap-1 sm:gap-1.5 p-1 sm:p-1.5 rounded-xl sm:rounded-[20px] overflow-x-auto w-full"
                                    style={{
                                        background: "rgba(23,31,53,0.04)",
                                        scrollbarWidth: "none",
                                        msOverflowStyle: "none",
                                    }}
                                >
                                    <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
                                    {data.tabs.map((tab) => (
                                        <TabButton
                                            key={tab.key}
                                            tab={tab}
                                            isActive={activeTab === tab.key}
                                            themeColors={themeColors}
                                            onClick={() => setActiveTab(tab.key)}
                                            t={t}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Feature Cards */}
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeTab}
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -12 }}
                                    transition={{ duration: 0.3 }}
                                    className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 w-full"
                                >
                                    {data.features[activeTab]?.map((feature, index) => (
                                        <FeatureCard
                                            key={`${activeTab}-${index}`}
                                            feature={feature}
                                            index={index}
                                            activeTab={activeTab}
                                            themeColors={themeColors}
                                            t={t}
                                        />
                                    ))}
                                </motion.div>
                            </AnimatePresence>

                           
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* ── Restock Modal ── */}
            <AnimatePresence>
                {isRestockModalOpen && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsRestockModalOpen(false)}
                    >
                        <motion.div
                            className="relative w-full max-w-md bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-100 overflow-hidden text-center flex flex-col items-center"
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", stiffness: 300, damping: 25 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Background Glow */}
                            <div
                                className="absolute -top-24 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full pointer-events-none blur-3xl opacity-30"
                                style={{ background: themeColors.gradient }}
                            />

                            {/* Close Button */}
                            <button
                                onClick={() => setIsRestockModalOpen(false)}
                                className="absolute top-4 left-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                                aria-label="بستن"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            {/* Animated Icon Badge */}
                            <div className="relative mb-5 mt-2 flex items-center justify-center">
                                <motion.div
                                    className="absolute inset-0 rounded-full"
                                    style={{ background: themeColors.primaryAlpha }}
                                    animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                />
                                <div
                                    className="relative w-20 h-20 rounded-2xl flex items-center justify-center text-white shadow-lg"
                                    style={{ background: themeColors.gradient }}
                                >
                                    <Clock className="w-10 h-10 animate-pulse" />
                                </div>
                            </div>

                            {/* Badge Tag */}
                            <div
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold mb-3"
                                style={{
                                    background: themeColors.primaryAlpha,
                                    color: themeColors.primary,
                                }}
                            >
                                <BellRing className="w-3.5 h-3.5" />
                                <span>اطلاعیه موجودی محصول</span>
                            </div>

                            {/* Modal Title */}
                            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">
                                به زودی مجدد موجود میشود
                            </h3>

                            {/* Modal Description */}
                            <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-6 font-medium">
                                ساعت هوشمند سالمندان دلبند با استقبال بی‌نظیر کاربران مواجه شده و در حال حاضر در فرآیند تامین مجدد می‌باشد. به زودی امکان ثبت سفارش فعال خواهد شد.
                            </p>

                            {/* Modal CTA */}
                            <motion.button
                                onClick={() => setIsRestockModalOpen(false)}
                                className="w-full py-3.5 px-6 rounded-2xl text-white font-bold text-base shadow-lg transition-all cursor-pointer"
                                style={{ background: themeColors.gradient }}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                متوجه شدم
                            </motion.button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default ProductIntro;